import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BellPlus, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Lets a visitor (logged in or anonymous) save their current search filters
 * and get emailed when new matching properties are listed. Logged-in users
 * save instantly (we already have their email); anonymous visitors are
 * asked for one first.
 */
export default function SaveSearchButton({ filters }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const hasLocation = !!(filters.city || filters.locality);

  const save = async (emailOverride) => {
    setBusy(true);
    try {
      await api.post("/saved-searches", { ...filters, email: emailOverride });
      setSaved(true);
      setOpen(false);
      toast.success("Saved! We'll email you when new matches go live.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onClick = () => {
    if (!hasLocation) { toast.error("Add a city or locality to save this search."); return; }
    if (user) save(undefined);
    else setOpen(true);
  };

  const submitEmail = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    save(email.trim());
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={onClick}
        disabled={busy || saved}
        data-testid="save-search-btn"
        className="rounded-full h-10 px-4 border-brand-subtle text-sm shrink-0"
      >
        {saved ? <Check size={14} className="mr-1.5 text-emerald-600" /> : <BellPlus size={14} className="mr-1.5 brand-forest" />}
        {saved ? "Saved — we'll email you" : "Save search & get alerts"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="save-search-email-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl brand-forest">Get email alerts</DialogTitle>
            <DialogDescription>
              We'll email you when new matching properties go live. No spam, unsubscribe anytime.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEmail} className="space-y-3">
            <Input
              data-testid="save-search-email-input"
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11"
            />
            <Button
              type="submit"
              data-testid="save-search-email-submit"
              disabled={busy}
              className="w-full tlr-gold-btn rounded-full h-11 font-semibold"
            >
              {busy ? "Saving…" : "Notify me"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
